import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { LifeAreaForm } from './LifeAreaForm';
import { WheelVisualization } from './WheelVisualization';
import { AdSpace } from './AdSpace';
import { BookShowcase } from './BookShowcase';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Download } from 'lucide-react';
import { trackRatingChange, trackStepProgression, trackWheelDownload, trackColorChange } from '../analytics';

export interface LifeArea {
  id: string;
  name: string;
  description: string;
  rating: number;
  color?: string;
  customName?: string;
  customDescription?: string;
}

export function WheelOfLife() {
  const { t, i18n } = useTranslation();
  const [currentStep, setCurrentStep] = useState<'rate' | 'view'>('rate');

  // Generate life areas from translations
  const lifeAreaIds = [
    'career', 'finance', 'health', 'family', 'relationships', 'social',
    'growth', 'recreation', 'environment', 'contribution', 'spirituality', 'mental'
  ];

  // Default colors for each area
  const defaultColors = [
    '#ef4444', '#f97316', '#eab308', '#22c55e',
    '#10b981', '#06b6d4', '#3b82f6', '#6366f1',
    '#8b5cf6', '#a855f7', '#d946ef', '#ec4899'
  ];

  const [lifeAreas, setLifeAreas] = useState<LifeArea[]>(() =>
    lifeAreaIds.map((id, index) => ({
      id,
      name: t(`lifeAreas.${id}.name`),
      description: t(`lifeAreas.${id}.description`),
      rating: 5,
      color: defaultColors[index],
      customName: undefined,
      customDescription: undefined
    }))
  );

  // Update life areas when language changes (but keep custom names/descriptions)
  useEffect(() => {
    setLifeAreas(prev => 
      prev.map((area, index) => ({
        ...area,
        name: area.customName || t(`lifeAreas.${area.id}.name`),
        description: area.customDescription || t(`lifeAreas.${area.id}.description`)
      }))
    );
  }, [i18n.language, t]);

  const updateRating = (id: string, rating: number) => {
    setLifeAreas(prev => 
      prev.map(area => 
        area.id === id ? { ...area, rating } : area
      )
    );
    
    // Track rating change
    trackRatingChange(id, rating);
  };

  const updateColor = (id: string, color: string) => {
    setLifeAreas(prev => 
      prev.map(area => 
        area.id === id ? { ...area, color } : area
      )
    );
    trackColorChange(id, color);
  };

  const updateName = (id: string, name: string) => {
    setLifeAreas(prev => 
      prev.map(area => 
        area.id === id ? { ...area, name, customName: name } : area
      )
    );
  };

  const updateDescription = (id: string, description: string) => {
    setLifeAreas(prev => 
      prev.map(area => 
        area.id === id ? { ...area, description, customDescription: description } : area
      )
    );
  };

  const handleStepChange = (step: 'rate' | 'view') => {
    setCurrentStep(step);
    trackStepProgression(step);
    // Scroll back to the top so the user sees the wheel (or the form) from the start.
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const downloadWheel = () => {
    const wheelElement = document.getElementById('wheel-visualization');
    if (!wheelElement) return;

    // Track wheel download
    trackWheelDownload();

    // Create a canvas and draw the SVG
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const svgData = new XMLSerializer().serializeToString(wheelElement.querySelector('svg')!);
    const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
    const svgUrl = URL.createObjectURL(svgBlob);

    const img = new Image();
    img.onload = () => {
      canvas.width = 800;
      canvas.height = 800;
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      
      const link = document.createElement('a');
      link.download = 'wheel-of-life.png';
      link.href = canvas.toDataURL();
      link.click();
      
      URL.revokeObjectURL(svgUrl);
    };
    img.src = svgUrl;
  };

  return (
    <div className="max-w-7xl mx-auto">
      {currentStep === 'rate' ? (
        <div className="flex gap-8">
          {/* Main Content */}
          <div className="flex-1 space-y-6">
            <LifeAreaForm 
              lifeAreas={lifeAreas} 
              onUpdateRating={updateRating}
              onUpdateColor={updateColor}
              onUpdateName={updateName}
              onUpdateDescription={updateDescription}
              onComplete={() => handleStepChange('view')}
            />
          </div>
          
          {/* Sidebar Ad - Only visible on larger screens */}
          {/* <div className="hidden lg:block w-80 space-y-6">
            <div className="sticky top-8">
              <AdSpace position="sidebar" />
            </div>
          </div> */}
        </div>
      ) : (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <Button 
              variant="outline" 
              onClick={() => handleStepChange('rate')}
            >
              {t('wheel.editRatings')}
            </Button>
            <Button onClick={downloadWheel} className="gap-2">
              <Download className="h-4 w-4" />
              {t('wheel.downloadWheel')}
            </Button>
          </div>
          
          <Card className="p-6">
            <div id="wheel-visualization">
              <WheelVisualization lifeAreas={lifeAreas} />
            </div>
          </Card>
          
          {/* Post-completion Ad - After user gets value */}
          {/* <AdSpace position="post-completion" className="my-8" /> */}
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {lifeAreas.map(area => (
              <Card key={area.id} className="p-4">
                <div className="flex justify-between items-center mb-2">
                  <h3 className="text-sm font-medium">{area.name}</h3>
                  <span className="text-lg font-semibold text-primary">{area.rating}/10</span>
                </div>
                <p className="text-xs text-muted-foreground">{area.description}</p>
              </Card>
            ))}
          </div>

          {/* Curated book recommendations - shown after the user gets their result */}
          <BookShowcase className="mt-10" />
        </div>
      )}
    </div>
  );
}